import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Star, Plus, Play, Image, Loader2, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Testimonials = () => {
  const { t } = useLanguage();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    author_name: '',
    author_company: '',
    content: '',
    rating: 5,
    media_url: '',
    media_type: ''
  });

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/testimonials`);
      setTestimonials(response.data);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.author_name || !formData.content) {
      toast.error('Veuillez remplir les champs requis');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/testimonials`, formData);
      toast.success('Témoignage soumis ! Il sera publié après validation.');
      setDialogOpen(false);
      setFormData({
        author_name: '',
        author_company: '',
        content: '',
        rating: 5,
        media_url: '',
        media_type: ''
      });
      fetchTestimonials();
    } catch (error) {
      console.error('Error submitting testimonial:', error);
      toast.error(t('error'));
    }
    setSubmitting(false);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const mediaType = file.type.startsWith('video/') ? 'video' : 'image';
      setFormData(prev => ({
        ...prev,
        media_url: `${API}/uploads/${response.data.filename}`,
        media_type: mediaType
      }));
      toast.success('Fichier uploadé');
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Erreur lors de l\'upload');
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${i < rating ? 'text-amber-400 fill-amber-400' : 'text-slate-300'}`}
      />
    ));
  };

  return (
    <div className="min-h-screen pt-24 pb-16 bg-slate-50" data-testid="testimonials-page">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <MessageSquare className="w-8 h-8 text-primary" />
          </div>
          <h1 className="font-heading font-bold text-3xl md:text-4xl text-slate-900 mb-4">
            {t('testimonials')}
          </h1>
          <p className="text-slate-600 max-w-2xl mx-auto mb-8">
            Découvrez ce que nos clients disent d'AGENTORY et partagez votre expérience
          </p>

          {/* Add Testimonial Button */}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-primary-hover text-white rounded-full gap-2" data-testid="add-testimonial-btn">
                <Plus className="w-5 h-5" />
                {t('addTestimonial')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{t('addTestimonial')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="testimonial-form">
                <div className="space-y-2">
                  <Label>{t('name')} *</Label>
                  <Input
                    value={formData.author_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, author_name: e.target.value }))}
                    placeholder="Votre nom"
                    data-testid="testimonial-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Entreprise</Label>
                  <Input
                    value={formData.author_company}
                    onChange={(e) => setFormData(prev => ({ ...prev, author_company: e.target.value }))}
                    placeholder="Nom de votre entreprise"
                    data-testid="testimonial-company-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('yourRating')}</Label>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, rating: star }))}
                        className="p-1"
                        data-testid={`testimonial-star-${star}`}
                      >
                        <Star
                          className={`w-6 h-6 transition-colors ${
                            star <= formData.rating
                              ? 'text-amber-400 fill-amber-400'
                              : 'text-slate-300'
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('message')} *</Label>
                  <Textarea
                    value={formData.content}
                    onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                    placeholder="Partagez votre expérience avec AGENTORY..."
                    rows={4}
                    data-testid="testimonial-content-textarea"
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('uploadMedia')}</Label>
                  <Input
                    type="file"
                    accept="image/*,video/*"
                    onChange={handleFileUpload}
                    data-testid="testimonial-media-input"
                  />
                  {formData.media_url && (
                    <p className="text-sm text-green-600">Fichier uploadé avec succès</p>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-primary hover:bg-primary-hover text-white"
                  data-testid="testimonial-submit-btn"
                >
                  {submitting ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    t('submit')
                  )}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Testimonials Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : testimonials.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-slate-500">Aucun témoignage pour le moment. Soyez le premier !</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <Card 
                key={testimonial.id} 
                className="card-hover animate-fade-in"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`testimonial-card-${testimonial.id}`}
              >
                <CardContent className="p-6">
                  {/* Media */}
                  {testimonial.media_url && (
                    <div className="mb-4 rounded-lg overflow-hidden">
                      {testimonial.media_type === 'video' ? (
                        <div className="relative aspect-video bg-slate-100">
                          <video
                            src={testimonial.media_url}
                            className="w-full h-full object-cover"
                            controls
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/20 pointer-events-none">
                            <Play className="w-12 h-12 text-white" />
                          </div>
                        </div>
                      ) : (
                        <img
                          src={testimonial.media_url}
                          alt={testimonial.author_name}
                          className="w-full h-48 object-cover"
                        />
                      )}
                    </div>
                  )}

                  {/* Rating */}
                  <div className="flex gap-0.5 mb-3">
                    {renderStars(testimonial.rating)}
                  </div>

                  {/* Content */}
                  <p className="text-slate-600 text-sm leading-relaxed mb-4">
                    "{testimonial.content}"
                  </p>

                  {/* Author */}
                  <div className="flex items-center gap-3 pt-4 border-t">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                      <span className="text-primary font-semibold">
                        {testimonial.author_name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{testimonial.author_name}</p>
                      {testimonial.author_company && (
                        <p className="text-sm text-slate-500">{testimonial.author_company}</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
